import { useLayoutEffect } from "react";

/**
 * Adds `<meta name="viewport" content="width=device-width, initial-scale=1">` to the TOP-LEVEL
 * document for as long as the dashboards route is mounted.
 *
 * Real mobile browsers ignore the `<meta viewport>` inside the dashboard iframe's srcDoc — only
 * the top-level document's viewport meta affects layout on a phone. The rest of the webapp SPA
 * has no viewport meta and hasn't been mobile-tested, so we inject/remove it per-route instead of
 * adding it to the shared index.html (which would affect every route in the app).
 *
 * This must run from the ROUTE WRAPPER, not from WebDashboardViewer itself: the wrapper renders
 * loading and error states (LoadingRoute, the crash fallback, the access gate) before the viewer
 * ever mounts. Without the meta, a phone lays those out against the 980px fallback viewport, so
 * `100vw`/`100vh` resolve to ~980x1669 instead of the screen and anything centred in them — e.g.
 * LoadingRoute's spinner — is pushed off toward the bottom-right of the visible area.
 *
 * useLayoutEffect (not useEffect) so the meta is in place before the first paint of those states,
 * avoiding a visible flash of the mis-scaled layout.
 */
export const useDashboardViewportMeta = (): void => {
  useLayoutEffect(() => {
    if (document.head.querySelector('meta[name="viewport"]')) {
      // Something else already provides one — don't duplicate or fight it.
      return;
    }
    const meta = document.createElement("meta");
    meta.name = "viewport";
    meta.content = "width=device-width, initial-scale=1";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
};
